import { useCanViewEmails } from '../../../helpers/useCanViewEmails';
import { useSnackbar } from 'material-ui-snackbar-provider';
import React, { useEffect, useMemo, useState } from 'react';
import { Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LinearProgress from '@mui/material/LinearProgress';
import { useDebounce } from 'usehooks-ts';

import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { getGroupsThunk } from '../../../store/groups/groupsSlice';
import { GROUP_WITH_SUBGROUP, USER, USER_NEW } from '../../../enums/TYPES';
import { ROLES, RolesLowerCase } from '../../../enums/ROLES';
import FullPageLoader from '../../modules/FullPageLoader';
import { updateGroupUsers } from '../../../api/groupsApi';
import { getUsersByRolesArray } from '../../../api';
import { NOTIFICATION } from '../../../enums/ENUMS';
import SearchInput from '../../modules/SearchInput';
import RolePlate from '../../modules/RolePlate';
import { displayRole } from '../../../helpers';
import Avatar from '../../modules/Avatar';
import Modal from '../../modules/Modal';
import styles from './UpdateParticipantModal.module.scss';

type TUpdateParticipantModal = {
  open: boolean;
  switchModal: () => void;
  group?: GROUP_WITH_SUBGROUP;
  setFetchTrigger: (num: number) => void;
};

const UpdateParticipantModal: React.FC<TUpdateParticipantModal> = ({ open, switchModal, group, setFetchTrigger }) => {
  const canViewEmails = useCanViewEmails();
  const notification = useSnackbar();
  const [newAnalysts, setNewAnalysts] = useState<USER_NEW[]>();
  const [newViewers, setNewViewers] = useState<USER_NEW[]>();
  const [newTitle, setNewTitle] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const currentRole = useAppSelector((state) => state.user.role);
  const [previousStep, setPreviousStep] = useState(0);

  const [step, setStep] = useState(currentRole === ROLES.ANALYST ? 3 : 1);
  const [searchAnalyst, setSearchAnalyst] = useState('');
  const debouncedValueAnalyst = useDebounce<string>(searchAnalyst, 500);
  const [searchViewer, setSearchViewer] = useState('');
  const debouncedValueViewer = useDebounce<string>(searchViewer, 500);

  const assignedAnalystsIds = useMemo(() => {
    return group?.analysts.map((analyst) => analyst.id);
  }, [group]);

  const assignedViewersIds = useMemo(() => {
    return group?.viewers.map((viewer) => viewer.id);
  }, [group]);

  const [addedAnalysts, setAddedAnalysts] = useState<string[]>([]);
  const [removedAnalysts, setRemovedAnalysts] = useState<string[]>([]);
  const [addedViewers, setAddedViewers] = useState<string[]>([]);
  const [removedViewers, setRemovedViewers] = useState<string[]>([]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      await getUsersByRolesArray([RolesLowerCase.ORGANIZATION_ADMIN, RolesLowerCase.ANALYST], debouncedValueAnalyst).then((res) =>
        setNewAnalysts(res.documents),
      );
      await getUsersByRolesArray([RolesLowerCase.VIEWER], debouncedValueViewer).then((res) => setNewViewers(res.documents));
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchUsers();
  }, [group, debouncedValueAnalyst, debouncedValueViewer]);

  const [selectAllOa, setSelectAllOa] = useState(false);
  const [selectAllAnalyst, setSelectAllAnalyst] = useState(false);

  const onSearchAnalyst = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchAnalyst(e.target.value);
  };
  const onSearchViewer = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchViewer(e.target.value);
  };

  const removeUser = (e: any) => {
    if (e.target.name === 'analyst') {
      const alreadyUnchecked = removedAnalysts.includes(e.target.id);
      if (alreadyUnchecked) {
        setRemovedAnalysts(removedAnalysts.filter((id) => id !== e.target.id));
      } else {
        setRemovedAnalysts([...removedAnalysts, e.target.id]);
      }
    }
    if (e.target.name === 'viewer') {
      const alreadyUnchecked = removedViewers.includes(e.target.id);
      if (alreadyUnchecked) {
        setRemovedViewers(removedViewers.filter((id) => id !== e.target.id));
      } else {
        setRemovedViewers([...removedViewers, e.target.id]);
      }
    }
  };

  const onSelectAll = (e: React.ChangeEvent<HTMLInputElement>, role: RolesLowerCase) => {
    if (role === RolesLowerCase.ORGANIZATION_ADMIN) {
      const oasIds = newAnalysts?.filter((oa) => oa.role.title === RolesLowerCase.ORGANIZATION_ADMIN).map((oa) => oa.user.id) || [];

      if (!selectAllOa) {
        setAddedAnalysts([...addedAnalysts, ...oasIds]);
      } else {
        setAddedAnalysts([...addedAnalysts.filter((oa) => !oasIds.includes(oa))]);
        setRemovedAnalysts([...removedAnalysts, ...oasIds]);
      }
      setSelectAllOa(!selectAllOa);
    } else if (role === RolesLowerCase.ANALYST) {
      const analystsIds =
        newAnalysts?.filter((analyst) => analyst.role.title === RolesLowerCase.ANALYST).map((analyst) => analyst.user.id) || [];

      if (!selectAllAnalyst) {
        setAddedAnalysts([...addedAnalysts, ...analystsIds]);
      } else {
        setAddedAnalysts([...addedAnalysts.filter((analyst) => !analystsIds.includes(analyst))]);
        setRemovedAnalysts([...removedAnalysts, ...analystsIds]);
      }
      setSelectAllAnalyst(!selectAllAnalyst);
    } else if (role === RolesLowerCase.VIEWER) {
      const viewersIds = newViewers?.map((viewer) => viewer.user.id) || [];

      if (addedViewers.length !== newViewers?.length) {
        setAddedViewers([...addedViewers, ...viewersIds]);
      } else {
        setAddedViewers([]);
        setRemovedViewers(viewersIds);
      }
    }
  };

  const addUser = (e: any) => {
    if (e.target.name === 'analyst') {
      const alreadyChecked = addedAnalysts.includes(e.target.id);
      if (alreadyChecked) {
        setAddedAnalysts(addedAnalysts.filter((id) => id !== e.target.id));
      } else {
        setAddedAnalysts([...addedAnalysts, e.target.id]);
      }
    }

    if (e.target.name === 'viewer') {
      const alreadyChecked = addedViewers.includes(e.target.id);
      if (alreadyChecked) {
        setAddedViewers(addedViewers.filter((id) => id !== e.target.id));
      } else {
        setAddedViewers([...addedViewers, e.target.id]);
      }
    }
  };

  const onModalClose = () => {
    switchModal();
    setAddedAnalysts([]);
    setRemovedAnalysts([]);
    setAddedViewers([]);
    setRemovedViewers([]);
    setNewTitle('');
    setSelectAllOa(false);
    setSelectAllAnalyst(false);
    if (currentRole === ROLES.ORGANIZATION_ADMIN) {
      setStep(1);
    }
  };

  const toOaStep = () => {
    setPreviousStep(step);
    setStep(2);
  };

  const toAnalystStep = () => {
    setPreviousStep(step);
    newAnalysts?.filter((analyst) => analyst.role.title === RolesLowerCase.ANALYST).length ? setStep(3) : nextStep();
  };

  const nextStep = () => {
    setPreviousStep(step);
    newViewers?.length ? setStep(4) : onSubmit();
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    setNewTitle(e.target.value);
  };

  const onSubmit = async () => {
    setLoading(true);
    try {
      await updateGroupUsers({
        title: newTitle.trim() || group!.title,
        groupdId: group!.id,
        usersToRemove: [...removedAnalysts, ...removedViewers],
        usersToAdd: [...addedAnalysts, ...addedViewers],
      });
      notification.showMessage(NOTIFICATION.UPDATED);
      setStep(5);
    } catch (err) {
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err.response.data.message);
      } else {
        notification.showMessage(NOTIFICATION.SOMETHING_WENT_WRONG);
      }
    } finally {
      setFetchTrigger(Date.now());
      setLoading(false);
    }
  };

  const stepBack = () => {
    if (previousStep) {
      setStep(previousStep);
      setPreviousStep(0);
    } else if (currentRole === ROLES.ANALYST ? 3 : 1) {
      onModalClose();
    } else {
      setStep(1);
    }
  };

  const [progress, setProgress] = useState(25);

  useEffect(() => {
    switch (step) {
      case 1:
        setProgress(25);
        break;
      case 2:
        setProgress(50);
        break;
      case 3:
        setProgress(75);
        break;
      case 4:
        setProgress(100);
        break;
      default:
        break;
    }
  }, [step]);

  const oAuserChips = useMemo(() => {
    const currentUsers = newAnalysts?.filter((user) => user.role.title === RolesLowerCase.ORGANIZATION_ADMIN);
    const filteredUsers = currentUsers?.filter(
      ({ user }) => (assignedAnalystsIds?.includes(user.id) && !removedAnalysts.includes(user.id)) || addedAnalysts.includes(user.id),
    );
    return filteredUsers?.map(({ user }) => (
      <div key={`chip_${user.id}`} className={styles.chip}>
        <span>{user?.username ? user.username : (canViewEmails ? user.email : 'User')}</span>{' '}
        <CloseIcon
          onClick={() =>
            assignedAnalystsIds?.includes(user.id)
              ? removeUser({
                  target: {
                    name: 'analyst',
                    id: user.id,
                  },
                })
              : addUser({
                  target: {
                    name: 'analyst',
                    id: user.id,
                  },
                })
          }
          className={styles.removeChip}
          sx={{ color: 'black', fontSize: '14px', marginLeft: '8px' }}
        />
      </div>
    ));
  }, [assignedAnalystsIds, newAnalysts, removedAnalysts, addedAnalysts]);

  const analystUserChips = useMemo(() => {
    const currentUsers = newAnalysts?.filter((user) => user.role.title === RolesLowerCase.ANALYST);
    const filteredUsers = currentUsers?.filter(
      ({ user }) => (assignedAnalystsIds?.includes(user.id) && !removedAnalysts.includes(user.id)) || addedAnalysts.includes(user.id),
    );
    return filteredUsers?.map(({ user }) => (
      <div key={`chip_${user.id}`} className={styles.chip}>
        <span>{user?.username ? user.username : (canViewEmails ? user.email : 'User')}</span>{' '}
        <CloseIcon
          onClick={() =>
            assignedAnalystsIds?.includes(user.id)
              ? removeUser({
                  target: {
                    name: 'analyst',
                    id: user.id,
                  },
                })
              : addUser({
                  target: {
                    name: 'analyst',
                    id: user.id,
                  },
                })
          }
          className={styles.removeChip}
          sx={{ color: 'black', fontSize: '14px', marginLeft: '8px' }}
        />
      </div>
    ));
  }, [assignedAnalystsIds, newAnalysts, removedAnalysts, addedAnalysts]);

  const viewerUserChips = useMemo(() => {
    const currentUsers = newViewers?.filter(
      (user) =>
        (user.role.title === RolesLowerCase.VIEWER &&
          assignedViewersIds?.includes(user.user.id) &&
          !removedViewers.includes(user.user.id)) ||
        addedViewers.includes(user.user.id),
    );
    return currentUsers?.map(({ user }) => (
      <div key={`chip_${user.id}`} className={styles.chip}>
        <span>{user?.username ? user.username : (canViewEmails ? user.email : 'User')}</span>{' '}
        <CloseIcon
          onClick={() =>
            assignedViewersIds?.includes(user.id)
              ? removeUser({
                  target: {
                    name: 'viewer',
                    id: user.id,
                  },
                })
              : addUser({
                  target: {
                    name: 'viewer',
                    id: user.id,
                  },
                })
          }
          className={styles.removeChip}
          sx={{ color: 'black', fontSize: '14px', marginLeft: '8px' }}
        />
      </div>
    ));
  }, [assignedViewersIds, newViewers, removedViewers, addedViewers]);

  return (
    <Modal open={open} onClose={onModalClose} loading={loading}>
      <FullPageLoader loading={loading} />
      {step === 1 && (
        <div className={styles.modalBody}>
          <div className={styles.modalTitle}>
            Edit Group Name
            <div className={styles.step}>Step {step}/4</div>
          </div>
          <LinearProgress className={styles.progressBar} variant="determinate" value={progress} />
          <input className={styles.field} defaultValue={group?.title} onChange={handleTitleChange} type="text" />
          <div className={styles.actionBtns}>
            <ContainedButton onClick={toOaStep} disabled={loading}>
              Next
            </ContainedButton>
            <OutlinedButton style={{ marginBottom: '0px' }} kind="default" onClick={onModalClose}>
              Cancel
            </OutlinedButton>
          </div>
        </div>
      )}

      {step === 2 && (
        <>
          <div className={`${styles.modalTitle} ${styles.modalTitleSection}`}>
            Edit Organisation Admins List
            <div className={styles.step}>Step {step}/4</div>
          </div>
          <LinearProgress className={styles.progressBar} variant="determinate" value={progress} />

          <div className={`${styles.modalBody} ${styles.modalBodyNoTopPadding}`}>
            <div className={styles.searchInput}>
              <SearchInput placeHolder="Search by email/name" className={styles.input} onChange={onSearchAnalyst} />
            </div>
            <div className={styles.userChips}>{oAuserChips}</div>
            <div className={styles.shadowHelper} />
            <div className={styles.usersList}>
              <label htmlFor="selectAllOa">
                <div className={styles.userLine}>
                  <div className={styles.userInfo}>
                    <div className={styles.info} style={{ marginLeft: '0px' }}>
                      <div className={styles.name}>Select All</div>
                    </div>
                  </div>
                  <input
                    id="selectAllOa"
                    onChange={(e) => onSelectAll(e, RolesLowerCase.ORGANIZATION_ADMIN)}
                    type="checkbox"
                    checked={selectAllOa}
                  />
                </div>
              </label>
              {newAnalysts
                ?.filter((user) => user.role.title === RolesLowerCase.ORGANIZATION_ADMIN)
                .map((person) => (
                  <label htmlFor={person.user.id} key={person.user.id}>
                    <div className={styles.userLine}>
                      <div className={styles.userInfo}>
                        <Avatar avatar={person?.user?.avatar_url} />
                        <div className={styles.info}>
                          <Tooltip title={person?.user?.username}>
                            <div className={styles.name}>{person?.user?.username}</div>
                          </Tooltip>
                          {canViewEmails && (<Tooltip title={person?.user?.email}>
                            <div className={styles.email}>{person?.user?.email}</div>
                          </Tooltip>)}
                        </div>
                      </div>
                      <div className={styles.horizontalContainer}>
                        <RolePlate userRole={displayRole(person.role.title)} />
                        <input
                          onChange={assignedAnalystsIds?.includes(person.user.id) ? removeUser : addUser}
                          name="analyst"
                          id={person.user.id}
                          type="checkbox"
                          checked={
                            (assignedAnalystsIds?.includes(person.user.id) && !removedAnalysts.includes(person.user.id)) ||
                            addedAnalysts.includes(person.user.id)
                          }
                        />
                      </div>
                    </div>
                  </label>
                ))}
            </div>
            <div className={styles.actionBtns}>
              <ContainedButton onClick={toAnalystStep} disabled={loading}>
                Next
              </ContainedButton>
              <OutlinedButton style={{ marginBottom: '0px' }} kind="default" onClick={stepBack}>
                Back
              </OutlinedButton>
            </div>
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <div className={`${styles.modalTitle} ${styles.modalTitleSection}`}>
            Edit Analysts List
            <div className={styles.step}>Step {step}/4</div>
          </div>
          <LinearProgress className={styles.progressBar} variant="determinate" value={progress} />

          <div className={`${styles.modalBody} ${styles.modalBodyNoTopPadding}`}>
            <div className={styles.searchInput}>
              <SearchInput placeHolder="Search by email/name" className={styles.input} onChange={onSearchAnalyst} />
            </div>
            <div className={styles.userChips}>{analystUserChips}</div>
            <div className={styles.shadowHelper} />
            <div className={styles.usersList}>
              <label htmlFor="selectAllAnalysts">
                <div className={styles.userLine}>
                  <div className={styles.userInfo}>
                    <div className={styles.info} style={{ marginLeft: '0px' }}>
                      <div className={styles.name}>Select All</div>
                    </div>
                  </div>
                  <input
                    id="selectAllAnalysts"
                    onChange={(e) => onSelectAll(e, RolesLowerCase.ANALYST)}
                    type="checkbox"
                    checked={selectAllAnalyst}
                  />
                </div>
              </label>
              {newAnalysts
                ?.filter((user) => user.role.title === RolesLowerCase.ANALYST)
                .map((person) => (
                  <label htmlFor={person.user.id} key={person.user.id}>
                    <div className={styles.userLine}>
                      <div className={styles.userInfo}>
                        <Avatar avatar={person?.user?.avatar_url} />
                        <div className={styles.info}>
                          <Tooltip title={person?.user?.username}>
                            <div className={styles.name}>{person?.user?.username}</div>
                          </Tooltip>
                          {canViewEmails && (<Tooltip title={person?.user?.email}>
                            <div className={styles.email}>{person?.user?.email}</div>
                          </Tooltip>)}
                        </div>
                      </div>
                      <div className={styles.horizontalContainer}>
                        <RolePlate userRole={displayRole(person.role.title)} />
                        <input
                          onChange={assignedAnalystsIds?.includes(person.user.id) ? removeUser : addUser}
                          name="analyst"
                          id={person.user.id}
                          type="checkbox"
                          checked={
                            (assignedAnalystsIds?.includes(person.user.id) && !removedAnalysts.includes(person.user.id)) ||
                            addedAnalysts.includes(person.user.id)
                          }
                        />
                      </div>
                    </div>
                  </label>
                ))}
            </div>
            <div className={styles.actionBtns}>
              <ContainedButton onClick={nextStep} disabled={loading}>
                Next
              </ContainedButton>
              <OutlinedButton style={{ marginBottom: '0px' }} kind="default" onClick={stepBack}>
                Back
              </OutlinedButton>
            </div>
          </div>
        </>
      )}

      {step === 4 && (
        <>
          <div className={`${styles.modalTitle} ${styles.modalTitleSection}`}>
            Edit Viewers List
            <div className={styles.step}>Step {step}/4</div>
          </div>
          <LinearProgress className={styles.progressBar} variant="determinate" value={progress} />
          <div className={`${styles.modalBody} ${styles.modalBodyNoTopPadding}`}>
            <div className={styles.searchInput}>
              <SearchInput placeHolder="Search by email/name" className={styles.input} onChange={onSearchViewer} />
            </div>
            <div className={styles.userChips}>{viewerUserChips}</div>
            <div className={styles.shadowHelper} />
            <div className={styles.usersList}>
              <label htmlFor="selectAllViewers">
                <div className={styles.userLine}>
                  <div className={styles.userInfo}>
                    <div className={styles.info} style={{ marginLeft: '0px' }}>
                      <div className={styles.name}>Select All</div>
                    </div>
                  </div>
                  <input id="selectAllViewers" onChange={(e) => onSelectAll(e, RolesLowerCase.VIEWER)} type="checkbox" />
                </div>
              </label>
              {newViewers?.map((person) => (
                <label htmlFor={person.user.id} key={person.user.id}>
                  <div className={styles.userLine}>
                    <div className={styles.userInfo}>
                      <Avatar avatar={person?.user?.avatar_url} />
                      <div className={styles.info}>
                        <Tooltip title={person?.user?.username}>
                          <div className={styles.name}>{person?.user?.username}</div>
                        </Tooltip>
                        {canViewEmails && (<Tooltip title={person?.user?.email}>
                          <div className={styles.email}>{person?.user?.email}</div>
                        </Tooltip>)}
                      </div>
                    </div>
                    <div className={styles.horizontalContainer}>
                      <RolePlate userRole={displayRole(person.role.title)} />
                      <input
                        onChange={assignedViewersIds?.includes(person.user.id) ? removeUser : addUser}
                        name="viewer"
                        id={person.user.id}
                        type="checkbox"
                        checked={
                          (assignedViewersIds?.includes(person.user.id) && !removedViewers.includes(person.user.id)) ||
                          addedViewers.includes(person.user.id)
                        }
                      />
                    </div>
                  </div>
                </label>
              ))}
            </div>
            <div className={styles.actionBtns}>
              <ContainedButton onClick={onSubmit} disabled={loading}>
                Update
              </ContainedButton>
              <OutlinedButton style={{ marginBottom: '0px' }} kind="default" onClick={stepBack}>
                Back
              </OutlinedButton>
            </div>
          </div>
        </>
      )}

      {step === 5 && (
        <div className={styles.successModal}>
          <div className={styles.title}>All Done 🎉</div>
          <ContainedButton onClick={onModalClose}>Close</ContainedButton>
        </div>
      )}
    </Modal>
  );
};

export default UpdateParticipantModal;
